import { useEffect, useState } from "react";
import { Dialog, IconButton } from "@sortera/react-components";
import { ArrowPathIcon, ArrowTopRightOnSquareIcon, DeviceDesktopIcon, SmartPhoneIcon, XMarkIcon } from "@sortera/react-icons";
import { apiUrl } from "../../integrations/http.ts";
import type { PreviewDto } from "../../types/index.ts";
import { builtText, type PreviewState } from "./utils.ts";
import styles from "./preview-panel.module.css";

export interface PreviewPanelProps {
  preview: PreviewDto | null;
  state: PreviewState;
  onClose: () => void;
}

type Device = "desktop" | "mobile";

/**
 * The app being built, next to the chat. Rebuilt by the runner after every turn that changed it.
 *
 * The frame is sandboxed twice: by the attribute here, and by the CSP the server sends with every
 * preview response. Neither grants `allow-same-origin`, so the preview runs as an origin of its
 * own and can never read this app's session — it is code the agent wrote, not code we reviewed.
 */
export function PreviewPanel({ preview, state, onClose }: PreviewPanelProps) {
  const [app, setApp] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>("desktop");
  const [reloads, setReloads] = useState(0);
  /** The preview lifted out, as a page of its own over everything. */
  const [lifted, setLifted] = useState(false);

  // Keep the chosen app across rebuilds, as long as it still exists.
  const apps = preview?.apps ?? [];
  const current = app && apps.includes(app) ? app : (apps[0] ?? null);
  useEffect(() => {
    if (app && !apps.includes(app)) setApp(null);
  }, [app, apps]);

  const src = preview && current ? apiUrl(`${preview.baseUrl}${current}/`) : null;
  const building = state.kind === "building";
  const seconds = useSecondsSince(building);
  const buildingText = src ? "Bygger vidare" : "Bygger förhandsvisningen";
  const status =
    state.kind === "building"
      ? "Bygger …"
      : state.kind === "failed"
        ? "Senaste bygget misslyckades"
        : preview
          ? builtText(preview.builtAt)
          : null;

  /** The same frame, same sandbox, in the panel and in the lifted view. */
  const frame = (className: string | undefined) =>
    src && (
      <iframe
        // A new build is a new document: remount, so nothing of the old one lingers.
        key={`${preview?.version}:${current}:${reloads}`}
        className={className}
        src={src}
        title={`Förhandsvisning av ${current}`}
        sandbox="allow-scripts allow-forms allow-popups allow-modals allow-downloads"
        referrerPolicy="no-referrer"
      />
    );

  /**
   * While the first build runs: one surface, with a light passing slowly across it, so the panel
   * reads as on its way rather than stuck — without guessing at a layout the app may not have. A
   * rebuild shows each version as it comes, with a note on top that the work goes on.
   */
  const buffering = (
    <div className={styles.buffering} role="status" aria-live="polite">
      <div className={styles.shimmer} aria-hidden />
      <p className={styles.bufferingText}>
        {buildingText} <span className={styles.seconds}>· {seconds} s</span>
      </p>
    </div>
  );

  const veil = (
    <div className={styles.veil} role="status" aria-live="polite">
      <span className={styles.pill}>
        <span className={styles.dot} aria-hidden />
        {buildingText} <span className={styles.seconds}>· {seconds} s</span>
      </span>
    </div>
  );

  /** What stands in for the app before there is one — in the panel and in the lifted view. */
  const empty = building ? buffering : (
    <div className={styles.empty}>
      <p className="sc-kicker">Inget att visa än</p>
      <p className={styles.emptyText}>
        När Claude har byggt en första vy visas appen här, och den uppdateras efter varje svar.
      </p>
    </div>
  );

  return (
    <section className={styles.panel} aria-label="Förhandsvisning">
      <header className={styles.bar}>
        <div className={styles.label}>
          <span className={styles.name}>Förhandsvisning</span>
          {status && (
            <span className={styles.status} data-state={state.kind} title={state.kind === "failed" ? state.message : undefined}>
              <span className={styles.dot} aria-hidden />
              {status}
            </span>
          )}
        </div>

        <div className={styles.tools}>
          {apps.length > 1 && (
            <div className={styles.apps} role="tablist" aria-label="App">
              {apps.map((name) => (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={name === current}
                  className={styles.app}
                  onClick={() => setApp(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
          <div className={styles.devices} role="group" aria-label="Skärmstorlek">
            {/* Already on the computer view, the same button lifts it out at full size. */}
            <IconButton
              aria-label={device === "desktop" ? "Öppna stort" : "Dator"}
              title={device === "desktop" ? "Öppna stort" : "Dator"}
              aria-pressed={device === "desktop"}
              color="secondary"
              size="sm"
              icon={<DeviceDesktopIcon />}
              onClick={() => (device === "desktop" ? setLifted(true) : setDevice("desktop"))}
            />
            <IconButton
              aria-label="Mobil"
              title="Mobil"
              aria-pressed={device === "mobile"}
              color="secondary"
              size="sm"
              icon={<SmartPhoneIcon />}
              onClick={() => setDevice("mobile")}
            />
          </div>
          <IconButton
            aria-label="Ladda om"
            title="Ladda om"
            color="secondary"
            size="sm"
            icon={<ArrowPathIcon />}
            disabled={!src}
            onClick={() => setReloads((n) => n + 1)}
          />
          {src && (
            <a className={styles.open} href={src} target="_blank" rel="noreferrer" title="Öppna i ny flik" aria-label="Öppna i ny flik">
              <ArrowTopRightOnSquareIcon size={18} />
            </a>
          )}
          <IconButton aria-label="Stäng förhandsvisningen" title="Stäng" color="secondary" size="sm" icon={<XMarkIcon />} onClick={onClose} />
        </div>
      </header>

      <div className={styles.stage} data-device={device}>
        {building && <span className={styles.progress} aria-hidden />}
        {src ? frame(styles.frame) : empty}
        {src && building && veil}
      </div>

      {state.kind === "failed" && preview && <p className={styles.note}>{state.message}</p>}

      <Dialog open={lifted} onOpenChange={setLifted}>
        <Dialog.Content className={styles.lifted} title={current ? `Förhandsvisning · ${current}` : "Förhandsvisning"} closeLabel="Stäng stora vyn">
          <div className={styles.liftedStage}>{src ? frame(styles.liftedFrame) : empty}</div>
        </Dialog.Content>
      </Dialog>
    </section>
  );
}

/** Whole seconds since `active` last turned on; zero while it is off. */
function useSecondsSince(active: boolean): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    setSeconds(0);
    if (!active) return;
    const started = Date.now();
    const timer = setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return seconds;
}
